/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * The single bridge from the staff panel into the domain layer.  CA-31.
 *
 * Same rule as apps/mobile/src/core.ts: no domain logic in the panel. What
 * the navigation is, who may see which item, when a session is about to end
 * and what a dirty form says before you leave it — all of that is core's,
 * tested there, and imported here. This is the one file that knows where
 * core lives.
 */
import adminShell from '../../../packages/core/admin-shell.js';
import forms from '../../../packages/core/forms.js';
import store from '../../../packages/core/store.js';
import shippingCore from '../../../packages/core/shipping.js';
import planner from '../../../packages/core/planner.js';
import day from '../../../packages/core/day.js';
import hijri from '../../../packages/core/hijri.js';
import tasks from '../../../packages/core/tasks.js';
import eventOps from '../../../packages/core/event-ops.js';
import requests from '../../../packages/core/requests.js';
import privacyPage from '../../../packages/core/privacy-page.js';
import jamaatRules from '../../../packages/core/jamaat-rules.js';
import bannersCore from '../../../packages/core/banners.js';
import socialCore from '../../../packages/core/social-share.js';
import whatsappCore from '../../../packages/core/whatsapp.js';
import votingCore from '../../../packages/core/board-voting.js';
import boardCore from '../../../packages/core/board-meetings.js';
import khutbahCore from '../../../packages/core/khutbah.js';
import dhikrCore from '../../../packages/core/dhikr.js';
import contactsCore from '../../../packages/core/contacts.js';
import touchLogCore from '../../../packages/core/touch-log.js';
import mosqueContactCore from '../../../packages/core/mosque-contact.js';
import expensesCore from '../../../packages/core/expenses.js';
import campaignsCore from '../../../packages/core/campaigns.js';
import qrCore from '../../../packages/core/qr.js';
import cashGiftsCore from '../../../packages/core/cash-gifts.js';
import budgetCore from '../../../packages/core/budget.js';
import printablesCore from '../../../packages/core/printables.js';
import jamaatGridCore from '../../../packages/core/jamaat-grid.js';

export interface NavItem {
  id: string;
  label_no: string;
  permission: string | null;
  route: string;
  parts?: Record<string, string>;
  /** Which heading the item is drawn under (core's GROUPS). */
  group?: string;
  /** The mosque capability the item also needs (core admin-shell.capabilityAllows). */
  capability?: string;
  /** Other permissions that also open the item (core admin-shell.permits): a list's manager without work.assigned. */
  alsoFor?: string[];
  /** Permissions the item ALSO needs, all of them (core admin-shell.permits): the screen's own first reads. */
  alsoNeeds?: string[];
}

/** A tab inside «Medlemmer»: shown when the person holds any of `anyOf`. */
export interface MemberTab { id: string; label_no: string; route: string; anyOf: string[] }

/** A heading and the items under it, as the sidebar draws them. */
export interface NavGroup {
  id: string;
  /** null for the first group, which is one item and needs no heading. */
  label_no: string | null;
  items: NavItem[];
}

/** A page inside a section: a NAV item, or one of the «Medlemmer» tabs (`tab`). */
export interface SectionPage { key: string; nav: string; tab: string | null; label_no: string; route: string }
/** One of the seven jobs the menu shows (core SECTIONS), with the pages this person may open. */
export interface Section { id: string; label_no: string; pages: SectionPage[] }

export const Shell = adminShell as unknown as {
  /** The menu's seven jobs, filtered to this person's pages; a job with none is absent. */
  sectionsFor(can: (permission: string) => boolean, caps: string[] | null): Section[];
  /** The section and page an address belongs to. */
  sectionOf(route: string, can: (permission: string) => boolean, caps: string[] | null): { section: Section | null; page: SectionPage | null };
  /** Per section: the work waiting for this person in it (from badges()). */
  sectionBadges(badges: Record<string, number>): Record<string, number>;
  NAV: NavItem[];
  GROUPS: { id: string; label_no: string | null }[];
  navGroups(items?: NavItem[]): NavGroup[];
  capabilityAllows(item: NavItem, caps: string[] | null): boolean;
  /** Where a capability shows up in the menu (PM gaps B1/B9); null for the member app only. `gates`: the item needs it. */
  capabilityPlace(key: string): { nav: string; group: string; item_no: string; group_no: string | null; gates: boolean } | null;
  permits(item: NavItem, can: (permission: string) => boolean): boolean;
  WARN_MINUTES_BEFORE_EXPIRY: number;
  badges(state: { openRequests?: number; pushesAwaitingApproval?: number; noticesAwaitingApproval?: number; membershipWork?: number }): Record<string, number>;
  /** The tabs inside «Medlemmer» (core MEMBER_TABS), filtered to the ones this person may open. */
  MEMBER_TABS: MemberTab[];
  memberTabsFor(can: (permission: string) => boolean): MemberTab[];
  memberRoute(route: string, can: (permission: string) => boolean): { tab: string | null; personId: string | null };
  /** The menu item an address belongs to (its route or one under it), and where a moved address now lives. */
  viewFor(route: string): { id: string; route: string; moved: boolean };
  sessionWarning(session: { expires_at: string } | null, now?: Date): { state: 'expired' | 'expiring'; minutesLeft?: number; message_no: string } | null;
  canLeave(dirty: boolean, target?: string): { ok: boolean; message_no?: string; confirmLabel_no?: string; cancelLabel_no?: string };
};

export interface FormField { name: string; label_no: string; options?: [string, string][] }
/** The service forms as members fill them in — labels and choices come from here, not a copy (fault 129). */
export const Forms = forms as unknown as { SCHEMAS: Record<string, { fields: FormField[] }> };

/** The shop's rules (core store.js): what a product and an order line may be, and a price typed in kroner. */
/** Shop shipping (2026-10-31): the carriers, their names, the rate table's rules and the tracking pages. */
export type Carrier = 'posten' | 'helthjem' | 'postnord' | 'other';
export interface ShippingRate { carrier: Carrier; maxWeightG: number; priceOre: number }
export const Shipping = shippingCore as unknown as {
  CARRIERS: Carrier[];
  /** What staff may choose now (Posten paused 2026-10-31). */
  OFFERED: Carrier[];
  isOffered(c: string): boolean;
  SHIPPING_MODES: string[];
  DEFAULT_WEIGHT_G: number;
  MAX_PRODUCT_WEIGHT_G: number;
  MAX_BAND_WEIGHT_G: number;
  MAX_RATE_ORE: number;
  MAX_RATES: number;
  MAX_PICKUP_POINT: number;
  label(carrier: string, lang?: string): string | null;
  trackingUrl(carrier: string, nr: string): string | null;
  validTrackingNumber(nr: unknown): boolean;
  rates(input: unknown): { ok: true; value: ShippingRate[] } | { ok: false; message: string };
  rateFor(rates: ShippingRate[], carrier: string, weightG: number): { carrier: Carrier; priceOre: number; maxWeightG: number } | null;
  shipInput(body: unknown): { ok: true; value: { carrier: Carrier; trackingNumber: string | null; trackingUrl: string | null; pickupPoint: string | null } } | { ok: false; errors: Record<string, string> };
};

export const Store = store as unknown as {
  MAX_TITLE: number;
  MAX_DESCRIPTION: number;
  MAX_QUANTITY: number;
  PRODUCT_STATES: string[];
  AVAILABILITY: string[];
  MAX_IMAGE_ALT: number;
  MAX_PRODUCTS: number;
  parseKroner(text: string): { ok: true; ore: number } | { ok: false; message: string };
  imageDescription(input: { decorative?: unknown; alt?: unknown }):
    { ok: true; value: { decorative: boolean; alt: string | null } } | { ok: false; errors: { alt: string } };
};

/** The annual planner's rules (core planner.js): categories, and a recurrence rule in words. MKK adoption Batch 1. */
export const Planner = planner as unknown as {
  CATEGORIES: string[];
  CATEGORY_NO: Record<string, string>;
  MAX_YEARS: number;
  describe(rule: unknown, item?: unknown): string | null;
  /** The Hijri month names (work hub, 2026-10-31), for a yearly item anchored to a Hijri day. */
  HIJRI_MONTHS_NO: string[];
};
/** Mosque-local calendar days as 'YYYY-MM-DD' text (core day.js): the same answer in every time zone. */
export const Day = day as unknown as {
  valid(s: string): boolean;
  parse(s: string): { y: number; m: number; d: number } | null;
  fmt(y: number, m: number, d: number): string;
  add(s: string, n: number): string;
  diff(a: string, b: string): number;
  weekday(s: string): number;
  daysInMonth(y: number, m: number): number;
  addMonths(y: number, m: number, k: number): { y: number; m: number };
};
/** The Hijri calendar (core hijri.js), shown beside a day as context. */
export const Hijri = hijri as unknown as {
  toHijri(date: Date, offsetDays?: number): { year: number; month: number; day: number };
  format(h: { year: number; month: number; day: number }, lang?: string): string;
  /** The mosque's moon-sighting adjustment in force on a day (core hijri.activeOffset). */
  activeOffset(adjustments: { effective_from: string; offset_days: number; created_at?: string }[], date: string): number;
};

/**
 * Status words, from core (UX consistency audit U-01/U-02/U-03, 2026-10-03). The panel used to re-declare each map
 * beside core's own; the words now have one home and the panel adds only its colour (withTones).
 */
export const STATUS_WORDS: { task: Record<string, string>; registration: Record<string, string>; listState: Record<string, string>; request: Record<string, string> } = {
  task: (tasks as any).STATUS_NO, registration: (eventOps as any).STATUS_NO, listState: (eventOps as any).LIST_STATE_NO, request: (requests as any).STATUS_NO,
};
/** core's words with the panel's tag colour per key: { key: [word, tone] }. A key without a colour is grey. */
export function withTones<T extends string>(words: Record<string, string>, tones: Record<string, T>, fallback: T): Record<string, [string, T]> {
  return Object.fromEntries(Object.entries(words).map(([k, w]) => [k, [w, tones[k] || fallback]])) as Record<string, [string, T]>;
}

/** What may be published as the mosque's privacy page (core privacy-page.js): the server's own rule, asked first in the panel. */
export const PrivacyPage = privacyPage as unknown as {
  MAX_BYTES: number;
  validate(input: string): { ok: boolean; code?: string; message_no?: string; title?: string; bytes?: number };
};

/** A jamaat period's rules (core jamaat-rules.js, 2026-10-15): the server's own check, asked first in the panel. */
export const Jamaat = jamaatRules as unknown as {
  FIELDS: string[];
  PRAYERS: string[];
  JUMUA: string[];
  MAX_OFFSET: number;
  LABEL_MAX: number;
  LABEL_NO: Record<string, string>;
  isOffset(v: unknown): boolean;
  describe(v: string | null | undefined): string;
  validatePeriod(input: Record<string, unknown>): {
    ok: boolean; errors: { field: string; code: string; message: string }[]; warnings: { field: string; code: string; message: string }[];
    period: Record<string, string | null>;
  };
  /** «Fast Jumu'ah-tid» (owner 2026-11-01): the every-Friday khutbah/jamaat, checked as the server checks it. */
  validateFixedJumua(input: Record<string, unknown>): {
    ok: boolean; errors: { field: string; code: string; message: string }[]; value: { jumua_khutbah: string | null; jumua_jamaat: string | null };
  };
};

/** Banner templates and their decorations (core banners.js + banner-art.js): the gallery draws its thumbnails from these. */
export interface BannerDecor { id: string; html: string; inset: { top: number; right: number; bottom: number; left: number } }
export interface BannerRender {
  palette: { bg: string; fg: string; accent: string };
  layers: { id: string; text: string; sizePct: number; weight: number; colour: string; lang?: string; dir?: string; lineHeight?: number }[];
  decor: BannerDecor | null;
  /** «Bilde med tekst» (2026-10-31): the banner's own photo is drawn behind the pieces, covering the banner. */
  photo?: boolean;
}
export interface BannerLayerDef { id: string; role: string; maxChars: number; required: boolean; sizePct: number; weight: number; lines?: number; placeholder_no?: string; options?: string[] }
export interface BannerTemplate { id: string; name_no: string; decor?: string; palette?: string; starter?: string; arabic?: string; photo?: boolean; layers: BannerLayerDef[] }
export const BannerCore = bannersCore as unknown as {
  TEMPLATE_ORDER: string[];
  TEMPLATES: Record<string, BannerTemplate>;
  ARABIC: Record<string, { ar: string; name_no: string }>;
  PALETTES: Record<string, { bg: string; fg: string; accent: string; name_no: string }>;
  /** The studio's «Pynt» (2026-10-31): the ornaments any text design may carry, with their names. */
  ORNAMENTS: { id: string; name_no: string }[];
  /** `decor`: the studio's choice — undefined/null = the template's own, 'none', or an ornament id. */
  render: (template: string, values: Record<string, string>, opts: { palette: string; decor?: string | null; lang?: string }) => BannerRender | null;
  decorOf: (tpl: BannerTemplate | null | undefined, choice: string | null | undefined) => string | null;
};

/** «Del i sosiale medier» (core social-share.js): sizes, captions, and where everything goes in the picture. */
export interface ShareSize { id: string; w: number; h: number; text: number; label_no: string; for_no: string }
export interface ShareLayout {
  w: number; h: number; bg: string; fg: string; align: 'left' | 'center'; gap: number;
  box: { x: number; y: number; w: number; h: number };
  pieces: { x: number; y: number; w: number; h: number; svg: string }[];
  layers: { text: string; px: number; weight: number; colour: string; lineHeight: number; dir: string; lang: string | null }[];
}
export const Social = socialCore as unknown as {
  SIZES: Record<string, ShareSize>;
  SIZE_ORDER: string[];
  /** The screens' own shape, 1920×1080 (banner studio, 2026-10-31): «Last ned», and a banner as a program's picture. */
  SCREEN: ShareSize;
  NETWORKS: Record<string, { id: string; name_no: string; max: number; links: boolean }>;
  NETWORK_ORDER: string[];
  PICTURE_GROUND: string;
  JPEG_QUALITY: number;
  caption(input: { title: string; body: string; mosque: { name?: string; website?: string | null }; network: string }): { text: string; truncated: boolean; network: string };
  whatsappLink(text: string): string;
  shareable(kind: 'banner' | 'notice', item: Record<string, unknown> | null): boolean;
  noticeBanner(notice: { title_no: string }, mosqueShort: string): { template: string; palette: string; values: Record<string, string> };
  layout(r: BannerRender, size: { w: number; h: number }): ShareLayout | null;
  contain(iw: number, ih: number, w: number, h: number): { x: number; y: number; w: number; h: number };
  wrap(text: string, max: number, measure: (s: string) => number): string[];
  fileName(title: string, sizeId: string, prefix?: string): string;
};

/** WhatsApp, phase 1 (core whatsapp.js, 2026-10-24): the kinds, the limits, the link a target opens, the group rule. */
export interface WaTarget { id: string; kind: string; name: string; inviteUrl: string; note: string | null; sort: number; archivedAt: string | null }
export const WhatsAppCore = whatsappCore as unknown as {
  KINDS: string[]; KIND_NO: Record<string, string>; SOURCES: string[]; SOURCE_NO: Record<string, string>;
  MAX: { name: number; url: number; note: number; text: number };
  openLink(target: { kind: string; inviteUrl: string } | null, text?: string): string | null;
  webLink(text: string): string;
  canPrefillGroup(): { ok: false; reason_no: string; flow: string[] };
};

/** Proposals and voting in the board (MKK module 19 «Avstemninger», 2026-10-25): the limits and the words, from core. */
export const Voting = votingCore as unknown as {
  DEFAULT_OPTIONS: string[]; MODE_NO: Record<string, string>; MAJORITY_NO: Record<string, string>; STATUS_NO: Record<string, string>; OUTCOME_NO: Record<string, string>;
  MAX_TITLE: number; MAX_TEXT: number; MAX_OPTION: number; MIN_OPTIONS: number; MAX_OPTIONS: number; MIN_ELIGIBLE: number; MAX_MEETING_ITEM: number;
  countsText(options: string[], counts: number[]): string;
  /** Who votes (work hub, 2026-10-31): the board, or the members in the app. */
  SCOPES: string[]; SCOPE_NO: Record<string, string>;
};

/** The board's meetings (MKK module 18 «Styremøte», 2026-10-25): statuses, kinds and limits, from core. */
export const Board = boardCore as unknown as {
  STATUSES: string[]; DECISION_KINDS: string[]; ATTENDANCE: string[];
  STATUS_NO: Record<string, string>; KIND_NO: Record<string, string>; ATTENDANCE_NO: Record<string, string>;
  MAX_TITLE: number; MAX_PLACE: number; MAX_MINUTES: number; MAX_ITEM_TITLE: number; MAX_BACKGROUND: number; MAX_PROPOSAL: number;
  MAX_DECISION: number; MAX_NOTE: number;
  editable(status: string): boolean; decidable(status: string): boolean; deletable(status: string): boolean;
  /** «Fast møtedag» (work hub, 2026-10-31). */
  SERIES_FREQS: string[];
};

/** The 14-day jamaat grid (work hub, 2026-10-31; core jamaat-grid.js): a typed cell checked, a pasted column, Tab/Enter. */
export interface GridCell { prayer: string; key: string; begins: string | null; jamaat: string | null; source: string | null; overridden: boolean; flag: { code: string; value: string } | null }
export interface GridRow { date: string; isFriday: boolean; isClosed: boolean; missing: boolean; cells: Record<string, GridCell>;
  day?: Record<string, string | null>; override: { reason: string | null; isClosed: boolean; fields: string[] } | null }
export const JamaatGrid = jamaatGridCore as unknown as {
  PRAYERS: string[]; COLS: string[]; OVERRIDE_FIELDS: string[];
  cellInput(day: unknown, prayer: string, raw: string, nextDay?: unknown): { ok: true; value: string | null; patch: Record<string, string | null> } | { ok: false; code: string; message: string };
  pasteColumn(text: string): (string | null)[];
  move(row: number, col: number, key: string, rows: number, o?: { shift?: boolean }): { row: number; col: number } | null;
  remaining(override: unknown, key: string): { fields: string[]; empty: boolean };
};

/** The khutbah plan (MKK adoption A1, 2026-10-24): the slots and their names, the limits, the one line a card prints. */
export const KhutbahCore = khutbahCore as unknown as {
  SLOTS: string[]; SLOT_NO: Record<string, string>; MAX_TEXT: number; MAX_NOTES: number; PANEL_FRIDAYS: number;
  isFriday: (day: string) => boolean;
  input: (body: unknown) => { ok: true; value: Record<string, unknown> } | { ok: false; errors: Record<string, string> };
  line: (r: { topic?: string | null; khatib?: string | null; language?: string | null } | null) => string | null;
};

/** Tasbih-innsamling (owner 2026-11-02): the dhikr phrases, the limits and the collection check — the server checks again. */
export interface DhikrPhrase { id: string; name: string; target: number; ar: string; tr: string; no: string; en: string; ur: string }
export const Dhikr = dhikrCore as unknown as {
  PHRASES: DhikrPhrase[]; MAX_COUNT: number; LIMITS: { title: number; note: number; target: number };
  phrase: (id: string | null | undefined) => DhikrPhrase | null;
  checkCollection: (body: unknown) => { ok: true; value: { title: string; phraseId: string | null; target: number | null; endsOn: string | null; note: string | null } }
    | { ok: false; errors: { field: string; reason: string }[] };
};

/** The mosque's contacts (MKK module 13 «Kontakter», 2026-10-24): the categories and their Norwegian names, from core. */
export const Contacts = contactsCore as unknown as {
  CATEGORIES: string[]; CATEGORY_NO: Record<string, string>;
  /** 074 (2026-10-31): «Sist kontaktet» and the list's order, with their Norwegian names. */
  TOUCHED: string[]; TOUCHED_NO: Record<string, string>; SORTS: string[]; SORT_NO: Record<string, string>;
  MAX_NAME: number; MAX_ORGANISATION: number; MAX_ROLE: number; MAX_LANGUAGES: number; MAX_NOTES: number; MAX_TAGS: number; MAX_EMAIL: number; MAX_QUERY: number;
};

/** «Hvem vi har kontaktet» (touch log, 073): the channels and their Norwegian names, the note's limit, from core. */
export const TouchLog = touchLogCore as unknown as { CHANNELS: string[]; CHANNEL_NO: Record<string, string>; MAX_NOTE: number };

/** The mosque's «Kontakt» page (2026-10-30): its limits and the rules the server holds, from core (the panel checks first). */
export const MosqueContact = mosqueContactCore as unknown as {
  MAX_PEOPLE: number; MAX_NAME: number; MAX_DESIGNATION: number; MAX_ADDRESS: number; MAX_EMAIL: number; MAX_WEBSITE: number; PHONE_MAX: number;
  input: (body: unknown) => { ok: true; value: { profile: Record<string, string | null>; people: Record<string, string | null>[] } }
    | { ok: false; errors: Record<string, string>; code?: string };
};

/** Utlegg (MKK module 23, 2026-10-24): categories, statuses and their Norwegian names, kr ↔ øre and the account number, from core. */
export const Expenses = expensesCore as unknown as {
  CATEGORIES: string[]; CATEGORY_NO: Record<string, string>; STATUSES: string[]; STATUS_NO: Record<string, string>;
  MAX_TITLE: number; MAX_DESCRIPTION: number; MAX_NOTE: number; MAX_REF: number; MAX_RECEIPTS: number; MAX_RECEIPT_BYTES: number;
  kroner(input: string): { ok: true; ore: number } | { ok: false; message: string };
  formatKr(ore: number, o?: { grouped?: boolean }): string;
  kr(ore: number): string;
  accountNumber(v: string): { ok: true; value: string } | { ok: false; message: string };
  accountText(s: string): string;
  MAX_LINK_LABEL: number; MAX_LINK_DAYS: number; MAX_LINK_USES: number; DEFAULT_LINK_DAYS: number; LINK_STATE_NO: Record<string, string>;
};

/** Budsjett (money-pro G1, 2026-10-31): kinds, suggestions, kr → øre, from core. */
export const Budget = budgetCore as unknown as {
  KINDS: string[]; KIND_NO: Record<string, string>; SUGGESTIONS: Record<string, string[]>; MAX_CATEGORY: number; MAX_NOTE: number;
  kroner(input: string): { ok: true; ore: number } | { ok: false; message: string };
};

/** Innsamling (MKK module 21, 2026-10-29): purposes, statuses, phases and their Norwegian names, kr ↔ øre, from core. */
export const Campaigns = campaignsCore as unknown as {
  PURPOSES: string[]; PURPOSE_NO: Record<string, string>; STATUS_NO: Record<string, string>; PHASE_NO: Record<string, string>;
  DONATION_PURPOSE: Record<string, string>; MAX_TITLE: number; MAX_TEXT: number;
  kroner(input: string): { ok: true; ore: number } | { ok: false; message: string };
  formatKr(ore: number, o?: { grouped?: boolean }): string;
  kr(ore: number): string;
  barPercent(p: number): number;
};
/** The QR encoder (telethon, 2026-10-31): the same one the TV draws a campaign page's address with. */
export const Qr = qrCore as unknown as {
  MAX_BYTES: number;
  encode(text: string): boolean[][];
  draw(ctx: { fillStyle: string | CanvasGradient | CanvasPattern; fillRect(x: number, y: number, w: number, h: number): void }, m: boolean[][], o?: { scale?: number; quiet?: number }): number;
  pixels(m: boolean[][], o?: { scale?: number; quiet?: number }): number;
};
/** Kontantgaver (MKK module 22, 2026-10-29): purposes, states and their Norwegian names, the month lock, from core. */
export const CashGifts = cashGiftsCore as unknown as {
  PURPOSES: string[]; PURPOSE_NO: Record<string, string>; STATES: string[]; STATE_NO: Record<string, string>; KIND_NO: Record<string, string>;
  MAX_NOTE: number; MAX_NAME: number; MAX_CONTACT: number; MAX_CAMPAIGN: number; TAX_NOTE: string;
  validMonth(m: string): boolean;
};
/** «Utskrifter» (MKK #34, 2026-10-29): the Arabic/Urdu headings for the HTML page, the language list, the poster sizes. */
export interface PosterSize { id: string; w: number; h: number; text: number; mm: [number, number]; label_no: string }
export const Printables = printablesCore as unknown as {
  LANGS: string[]; DASH: string; MAX_DAYS: number;
  EXTRA_HEADINGS: Record<'ar' | 'ur', Record<string, string>>;
  POSTER_SIZES: Record<string, PosterSize>; POSTER_ORDER: string[]; POSTER_GROUND: string;
  posterFileName(title: string, sizeId: string): string;
};
